import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, FileText, LogOut, PenLine, Eraser, ShieldCheck, CheckCircle2, Check } from "lucide-react";
import api, { apiError, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { BrandMark } from "../components/Layout";

function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.4; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#111827";
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, rect.width, rect.height);
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const touch = e.touches?.[0];
    const cx = touch ? touch.clientX : e.clientX;
    const cy = touch ? touch.clientY : e.clientY;
    return { x: cx - rect.left, y: cy - rect.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p;
    onChange(canvasRef.current.toDataURL("image/png"));
  };
  const end = () => { if (drawing.current) { drawing.current = false; onChange(canvasRef.current.toDataURL("image/png")); } };
  const clear = () => {
    const canvas = canvasRef.current; const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, rect.width, rect.height);
    onChange(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        data-testid="signature-pad"
        className="w-full h-40 rounded-xl border border-zinc-300 bg-white cursor-crosshair"
        style={{ touchAction: "none" }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <button type="button" onClick={clear} data-testid="signature-clear-btn"
        className="mt-2 text-sm font-semibold text-zinc-500 hover:text-[#1A1A1A] inline-flex items-center gap-1.5">
        <Eraser size={15} /> Clear
      </button>
    </div>
  );
}

export default function SignAgreement() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const { t, lang } = useLang();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [docLang, setDocLang] = useState(lang === "nl" ? "nl" : "en");
  const [agree, setAgree] = useState(false);
  const [fullName, setFullName] = useState(user?.name || "");
  const [signature, setSignature] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/legal/nda/status")
      .then(({ data }) => {
        setStatus(data);
        if (Array.isArray(data.languages) && data.languages.length && !data.languages.includes(docLang)) {
          setDocLang(data.languages[0]);
        }
      })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const pdfUrl = `${API}/legal/nda/pdf?lang=${docLang}`;
  const canSubmit = agree && fullName.trim().length >= 2 && !!signature && !submitting;

  const submit = async () => {
    if (!canSubmit) {
      if (!signature) toast.error(t("Please draw your signature."));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/legal/nda/sign", { language: docLang, full_name: fullName.trim(), signature });
      toast.success(t("Agreement signed — continue to subscribe."));
      nav("/subscribe");
    } catch (e) {
      if (e?.response?.status === 403) toast.error(t("Only your company's billing owner can sign the agreement."));
      else toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-zinc-500"><Loader2 className="animate-spin" /></div>;

  const s = status || {};
  const canSign = s.can_sign === true;
  const alreadySigned = s.signed === true && s.required === false;
  const languages = Array.isArray(s.languages) && s.languages.length ? s.languages : ["en", "nl"];
  const langLabel = { en: t("English"), nl: t("Nederlands") };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col" data-testid="sign-agreement-page">
      <div className="surface-dark px-5 py-4 flex items-center justify-between border-b border-zinc-800/60">
        <BrandMark company={null} />
        <button onClick={logout} data-testid="sign-logout-btn" className="text-zinc-300 hover:text-white flex items-center gap-1.5 text-sm font-semibold"><LogOut size={16} /> {t("Log out")}</button>
      </div>

      <div className="flex-1 flex items-start justify-center px-5 py-10">
        <div className="w-full max-w-lg">
          <div className="flex items-center gap-2 text-[#F26722] font-bold text-sm"><ShieldCheck size={16} /> {t("Service agreement (NDA & AVG)")}</div>

          {alreadySigned ? (
            <div className="mt-4 bg-[#18181B] border border-zinc-800 rounded-2xl p-6 text-center" data-testid="already-signed">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto"><CheckCircle2 size={28} className="text-emerald-600" /></div>
              <h1 className="text-2xl font-extrabold text-[#1A1A1A] mt-4">{t("Agreement signed")}</h1>
              <p className="text-zinc-500 mt-2 text-sm">{t("Signed by")} {s.signed_by_name} {t("on")} {(s.signed_at || "").slice(0, 10)} ({(s.language || "").toUpperCase()})</p>
              <button onClick={() => nav("/subscribe")} data-testid="already-signed-continue" className="w-full bg-[#F26722] text-white font-bold py-3.5 rounded-xl mt-6 hover:bg-[#D95513] transition-colors">{t("Continue")}</button>
            </div>
          ) : (
            <>
              <h1 className="text-3xl font-extrabold text-[#1A1A1A] mt-3">{t("Sign the agreement to continue")}</h1>
              <p className="text-zinc-500 mt-3 text-sm">{t("Your free trial has ended. Before you can subscribe, your company must sign the service agreement covering the NDA and AVG (GDPR). One signature unblocks your whole company.")}</p>

              <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 mt-6">
                <p className="text-xs font-bold tracking-wider text-zinc-400 uppercase mb-2">{t("Choose your language")}</p>
                <div className="flex gap-2">
                  {languages.map((l) => (
                    <button key={l} onClick={() => setDocLang(l)} data-testid={`doc-lang-${l}`}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${docLang === l ? "bg-[#F26722] text-white border-[#F26722]" : "bg-transparent text-zinc-400 border-zinc-700 hover:text-[#1A1A1A]"}`}>
                      {langLabel[l] || l.toUpperCase()}
                    </button>
                  ))}
                </div>

                <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="read-agreement-btn"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#F26722] hover:underline"><FileText size={16} /> {t("Read the agreement (PDF)")}</a>
                <iframe title="agreement" src={pdfUrl} data-testid="agreement-pdf" className="w-full h-[320px] rounded-xl border border-zinc-300 bg-white mt-3" />
              </div>

              {canSign ? (
                <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 mt-5">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="agree-checkbox" className="mt-1 w-5 h-5 accent-[#F26722]" />
                    <span className="text-sm text-[#1A1A1A]">{t("I have read and agree to the NDA & AVG service agreement.")}</span>
                  </label>

                  <label className="block text-xs font-bold tracking-wider text-zinc-400 uppercase mt-5 mb-2">{t("Full name")}</label>
                  <input value={fullName} onChange={(e) => setFullName(e.target.value)} data-testid="full-name-input" placeholder={t("Enter your full legal name")}
                    className="w-full bg-white border border-zinc-300 text-[#1A1A1A] rounded-xl px-4 py-3 placeholder-zinc-400 focus:border-[#F26722] focus:outline-none" />

                  <label className="block text-xs font-bold tracking-wider text-zinc-400 uppercase mt-5 mb-2">{t("Draw your signature")}</label>
                  <SignaturePad onChange={setSignature} />

                  <button onClick={submit} disabled={!canSubmit} data-testid="sign-continue-btn"
                    className="w-full bg-[#F26722] text-white font-bold py-3.5 rounded-xl mt-5 hover:bg-[#D95513] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2">
                    {submitting ? <Loader2 className="animate-spin" size={18} /> : <><Check size={18} /> {t("Sign & continue")}</>}
                  </button>
                </div>
              ) : (
                <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-6 mt-5" data-testid="cannot-sign">
                  <p className="text-zinc-600">{t("Only your company's billing owner can sign the agreement.")}</p>
                </div>
              )}
            </>
          )}

          <p className="text-center text-sm text-zinc-500 mt-8">{t("Signed in as")} {user?.name} {user?.company_name ? `· ${user.company_name}` : ""}</p>
        </div>
      </div>
    </div>
  );
}
